import React, { useMemo } from "react";
import { X, CheckCircle2 } from "lucide-react";
import { Modal } from "antd";
import formatPrice from "../../../../utility/formatPrice";
import formatPrice0 from "../../../../utility/formatPrice0";
interface DirectPlan {
  name: string;
  subtitle: string;
  description: string;
  monthlyPrice: number;
  quarterlyPrice?: number;
  yearlyPrice: number;
  features: string[];
  integrationServices: Array<{ name: string; cost: string }>;
  tier: string;
  isRange?: boolean;
  contactSales?: boolean;
}

interface DirectPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: DirectPlan;
  /** Called with the selected tier AND the computed setup fee total (as a number) */
  onGetStarted: (tier: string, setupFee: number) => void;
  billingType: "Monthly" | "Quarterly" | "Yearly";
  isSelected?: boolean;
  onRemove?: () => void;
}

const DirectPlanModal: React.FC<DirectPlanModalProps> = ({
  isOpen,
  onClose,
  plan,
  onGetStarted,
  billingType,
  isSelected,
  onRemove,
}) => {
  /** Sum all parseable cost entries (skip "Custom" / "Contact Sales") */
  const setupFeeTotal = useMemo(() => {
    return plan.integrationServices
      .filter((s) => s.name && s.cost)
      .reduce((sum, s) => {
        const numeric = parseFloat(s.cost.replace(/[₦,\s]/g, ""));
        return sum + (isNaN(numeric) ? 0 : numeric);
      }, 0);
  }, [plan.integrationServices]);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={700}
      centered
      className="direct-plan-modal"
      closeIcon={<X className="text-gray-400 hover:text-gray-600" />}
    >
      <div className="p-2 font-abel">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-medium text-gray-900">{plan.name}</h2>
          <p className="text-blue-600 font-medium">{plan.subtitle}</p>
          <p className="text-gray-600 mt-2">{plan.description}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Core Features */}
          <div>
            <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
              <span className="w-2 h-6 bg-blue-600 rounded-full"></span>
              Core Features
            </h3>
            <ul className="space-y-3">
              {plan.features.map((feature, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm text-gray-700"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Deployment & Setup Cost */}
          <div>
            <span className="flex justify-between items-center">
              <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                <span className="w-2 h-6 bg-green-600 rounded-full"></span>
                Deployment &amp; Setup Cost
              </h3>
              <span
                className={`text-sm font-medium mb-4 pr-4 ${
                  plan.isRange ? "text-white" : "text-[#212121]"
                }`}
              >
                NGN
              </span>
            </span>
            <div className="max-h-[260px] overflow-y-auto pr-2 space-y-2">
              {plan.integrationServices
                .filter((s) => s.name)
                .map((service, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-start gap-4 p-2 bg-gray-50 rounded text-xs"
                  >
                    <span className="text-gray-700">{service.name}</span>
                    <span className="font-medium text-gray-700 whitespace-nowrap">
                      {service.cost}
                    </span>
                  </div>
                ))}
            </div>
            {/* Setup Fee Total */}
            {setupFeeTotal > 0 && (
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                <span className="text-sm font-semibold text-gray-800">
                  Total Setup Fee
                </span>
                <span className="text-sm font-bold text-green-700">
                  NGN {formatPrice0(setupFeeTotal)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-sm text-gray-500 font-abel">
              {billingType === "Monthly"
                ? "Monthly"
                : billingType === "Quarterly"
                  ? "Quarterly"
                  : "Yearly"}{" "}
              Subscription Price
            </span>
            <div className="flex items-baseline gap-1">
              {plan.contactSales ? (
                <span className="text-2xl font-medium text-gray-900">
                  Contact Sales
                </span>
              ) : (
                <>
                  <span className="text-3xl font-medium text-gray-900">
                    {billingType === "Monthly"
                      ? plan.monthlyPrice?.toLocaleString()
                      : billingType === "Quarterly"
                        ? (plan.quarterlyPrice || plan.monthlyPrice * 3).toLocaleString()
                        : plan.yearlyPrice?.toLocaleString()}
                  </span>
                  <span className="text-gray-500 text-sm">
                    /{billingType === "Monthly" ? "month" : billingType === "Quarterly" ? "quarter" : "year"}
                  </span>
                </>
              )}
            </div>
            {setupFeeTotal > 0 && (
              <span className="text-xs text-gray-500 mt-1">
                + NGN {formatPrice(setupFeeTotal)} setup (one-time)
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isSelected ? (
              <button
                onClick={onRemove}
                className="px-8 py-3 bg-white text-red-600 border border-red-600 rounded-lg font-medium hover:bg-red-50 transition shadow-sm"
              >
                Remove Plan
              </button>
            ) : (
              <button
                onClick={() => onGetStarted(plan.tier, setupFeeTotal)}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
              >
                {plan.contactSales ? "Contact Sales" : "Add to Plan"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DirectPlanModal;
